
import { Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";

import PostingModal from "@/components/postings/PostingModal.vue";
import CardBodyItem from "@/components/card/CardBodyItemNew.vue";
import Card from "@/components/card/Card.vue";
import Chip from "@/components/filters/Chip.vue";
import TableAvatar from "@/components/table/TableAvatar.vue";
import UiButton from "@/components/button/ui-button.vue";
import ApplicantChips from "@/components/recruitings/ApplicantChips.vue";
import PostingActivenessToggleButton from "@/components/recruitings/PostingActivenessToggleButton.vue";
import Badge from "@/components/common/Badge.vue";

import { Posting } from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import { Application } from "@/generated/graphql";
import DurationType from "@/entities/DurationType";
import BadgeColor from "@/entities/BadgeColor";

import ItemChipsData from "@/entities/ItemChipsData";
import getPostedAtLabel from "@/helpers/postings/getPostedAtLabel";
import getStatusLabel from "@/helpers/postings/getStatusLabel";
import getLocationInfo from "@/helpers/locations/getLocationInfo";
import ChipWithLinkData from "@/types/ChipWithLinkData";
import ChipsWithMoreNew from "@/components/common/ChipsWithMoreNew.vue";

@Component({
  components: {
    CardBodyItem,
    ApplicantChips,
    PostingActivenessToggleButton,
    Chip,
    Card,
    TableAvatar,
    PostingModal,
    UiButton,
    Badge,
    ChipsWithMoreNew,
  },
})
export default class PostingCard extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: Object, required: true }) readonly posting!: Posting;
  @Prop({ type: String, default: undefined }) readonly managerId!: string;
  @Prop({ type: Boolean, default: false }) readonly canEdit!: boolean;
  @Prop({ type: Boolean, default: false }) readonly inactiveManager!: boolean;
  @Prop({ type: String, required: true }) readonly companyId!: string;

  routes = RouteNames;

  dialog: boolean = false;

  get badgeData(): { value: string; color: string } {
    if (!this.posting.active) {
      return {
        value: "Not active",
        color: BadgeColor.WARNING,
      };
    }
    if (!this.applicants.length) {
      return {
        value: "No applicants yet",
        color: BadgeColor.INFO,
      };
    }
    const applicants = this.applicants.length == 1 ? "applicant" : "applicants";
    return {
      value: `${this.applicants.length} ${applicants}`,
      color: BadgeColor.SUCCESS,
    };
  }

  get postingDetailsRouteName(): string {
    return this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_DETAILS);
  }

  get type(): string | undefined {
    return this.posting.employment_type || undefined;
  }

  get location(): string {
    return getLocationInfo(this.posting);
  }

  get isDurationInterval(): boolean {
    return this.posting.duration_type === DurationType.INTERVAL;
  }

  get startDate(): string {
    return this.posting.start_date ? this.posting.start_date : "n/a";
  }

  get endDate(): string {
    return this.posting.end_date ? this.posting.end_date : "n/a";
  }

  get jobDuration(): string | undefined {
    const { duration } = this.posting;
    if (!duration) {
      return undefined;
    }

    const weeks = Math.floor(duration / 7);
    const days = duration % 7;

    const weeksVal = this.formatDateItem("week", weeks);
    const daysVal = this.formatDateItem("day", days);

    return `${weeksVal} ${daysVal}`;
  }

  get postedAt(): string {
    return getPostedAtLabel(this.posting.public);
  }

  get status(): string {
    return getStatusLabel(this.posting.active);
  }

  get managerRouteName(): string | undefined {
    return this.inactiveManager
      ? undefined /* If the RouteName is undefined, the manager chip will be inactive */
      : this.routes.MANAGER_ABOUT;
  }

  get applicants(): [] | ItemChipsData[] {
    if (!this.posting.applications) {
      return [];
    }
    return this.posting.applications.map(
      ({ user }: Application): ItemChipsData => {
        const { family_name, given_name, contracts } = user;
        const baseInfo = {
          id: user.id,
          name: `${given_name} ${family_name}`,
          avatar: user.avatar ?? undefined,
        };
        const contractorId = contracts[0]?.id;

        if (contractorId) {
          return {
            ...baseInfo,
            routeParams: {
              name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
              params: { id: contractorId },
            },
          };
        }

        return {
          ...baseInfo,
          routeParams: {
            name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
            params: { id: user.id },
          },
        };
      }
    );
  }

  get manager(): any {
    const user = this.posting.manager?.user;
    if (!user) {
      return undefined;
    }

    return {
      name: `${user.given_name} ${user.family_name}`,
      id: this.posting.manager!.id,
      avatar: user.avatar,
    };
  }

  formatDateItem(unitName: string, value: number): string {
    if (!value) {
      return "";
    }
    if (value !== 1) {
      unitName += "s";
    }
    return `${value} ${unitName}`;
  }

  get navigateToDetails(): Object {
    return {
      name: RouteNames.RECRUITING_POSTING,
      params: { id: this.posting.id },
    };
  }

  get navigateToApplications(): Object {
    return {
      name: RouteNames.RECRUITING_POSTING_APPLICATIONS,
      params: { id: this.posting.id },
    };
  }

  get industries(): any[] {
    if (!this.posting.industries) {
      return [];
    }
    return this.posting.industries.map(({ industry }: any) => {
      return {
        name: industry.name,
        id: industry.id,
      };
    });
  }

  get skills(): any[] {
    if (!this.posting.skills) {
      return [];
    }
    return this.posting.skills.map(({ skill }: any) => {
      return {
        name: skill.name,
        id: skill.id,
      };
    });
  }

  get skillss(): ChipWithLinkData[] {
    let skills = this.posting.skills.map(({ skill }) => skill);
    const skillss = skills.reduce((filtered, skill) => {
      let chipWithLinkData: ChipWithLinkData = {
        variant: "gray",
        chipLink: this.navigateToDetails,
        chipData: { id: skill.id.toString(), name: skill.name },
        avatar: "",
        chipText: "",
      };
      filtered.push(chipWithLinkData);
      return filtered;
    }, []);
    // skills.sort((a, b) => a.variant.localeCompare(b.variant));
    return skillss;
  }

  get industriess(): ChipWithLinkData[] {
    let industries = this.posting.industries.map(({ industry }) => industry);
    const industriess = industries.reduce((filtered, industry) => {
      let chipWithLinkData: ChipWithLinkData = {
        variant: "blue",
        chipLink: this.navigateToDetails,
        chipData: { id: industry.id.toString(), name: industry.name },
        avatar: "",
        chipText: "",
      };
      filtered.push(chipWithLinkData);
      return filtered;
    }, []);
    return industriess;
  }
}
